import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { ErrorNote, Avatar } from '../components/ui.jsx';

const STATUSES = [
  { value: 'PRESENT', label: 'P', full: 'Present', cls: 'present' },
  { value: 'HALF_DAY', label: 'H', full: 'Half-Day', cls: 'half' },
  { value: 'ABSENT', label: 'A', full: 'Absent', cls: 'absent' },
  { value: 'LEAVE', label: 'L', full: 'Leave', cls: 'leave' },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function Attendance() {
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [marks, setMarks] = useState({}); // employeeId -> { status, overtimeHours }
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    setError(''); setNotice('');
    api(`/attendance/day/${date}`).then((data) => {
      setRows(data);
      setMarks(Object.fromEntries(
        data.filter((r) => r.record).map((r) => [r.id, { ...r.record }])
      ));
    }).catch((e) => setError(e.message));
  };
  useEffect(() => { load(); }, [date]);

  const setStatus = (empId, status) =>
    setMarks((m) => ({ ...m, [empId]: { status, overtimeHours: status === 'PRESENT' ? (m[empId]?.overtimeHours ?? 0) : 0 } }));
  const setOT = (empId, hours) =>
    setMarks((m) => ({ ...m, [empId]: { ...(m[empId] ?? { status: 'PRESENT' }), overtimeHours: hours } }));

  const markAllPresent = () =>
    setMarks(Object.fromEntries(rows.map((r) => [r.id, { status: 'PRESENT', overtimeHours: marks[r.id]?.overtimeHours ?? 0 }])));

  const counts = useMemo(() => {
    const c = { PRESENT: 0, ABSENT: 0, HALF_DAY: 0, LEAVE: 0, unmarked: 0 };
    for (const r of rows) {
      const m = marks[r.id];
      if (m) c[m.status]++; else c.unmarked++;
    }
    return c;
  }, [rows, marks]);

  const save = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const entries = Object.entries(marks).map(([employeeId, m]) => ({
        employeeId, status: m.status, overtimeHours: Number(m.overtimeHours) || 0,
      }));
      const res = await api('/attendance/mark', { method: 'POST', body: { date, entries } });
      setNotice(`Saved attendance for ${res.saved} employees.`);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="att-controls">
        {/* Capture method — biometric integration lands in a later phase. */}
        <div className="segmented" title="Attendance capture method">
          <button className="on" type="button">✍ Manual</button>
          <button type="button" disabled>
            ☝ Fingerprint <span className="soon-chip">Soon</span>
          </button>
        </div>
        <input type="date" className="date-input" value={date} max={today()}
          onChange={(e) => setDate(e.target.value)} />
        <button className="btn btn-ghost" onClick={markAllPresent}>Mark all present</button>
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={save}
          disabled={busy || Object.keys(marks).length === 0}>
          {busy ? 'Saving…' : `Save (${Object.keys(marks).length})`}
        </button>
      </div>

      <div className="att-summary-bar">
        <div className="att-stat"><div className="att-stat-val text-green">{counts.PRESENT}</div><div className="att-stat-lbl">Present</div></div>
        <div className="att-stat"><div className="att-stat-val text-amber">{counts.HALF_DAY}</div><div className="att-stat-lbl">Half-Day</div></div>
        <div className="att-stat"><div className="att-stat-val text-red">{counts.ABSENT}</div><div className="att-stat-lbl">Absent</div></div>
        <div className="att-stat"><div className="att-stat-val text-muted">{counts.unmarked}</div><div className="att-stat-lbl">Unmarked</div></div>
      </div>

      <ErrorNote error={error} />
      {notice && <div className="notice-note">{notice}</div>}

      <div className="att-list">
        {rows.map((r) => {
          const m = marks[r.id];
          return (
            <div key={r.id} className="att-row">
              <Link to={`/employees/${r.id}`} title="View attendance history">
                <Avatar name={r.name} id={r.code} />
              </Link>
              <div className="att-info">
                <Link to={`/employees/${r.id}`} className="name" style={{ display: 'block' }}>{r.name}</Link>
                <div className="site">{r.designation} · {r.site.name}</div>
              </div>
              {m?.status === 'PRESENT' && (
                <select className="att-status-select" value={m.overtimeHours}
                  onChange={(e) => setOT(r.id, Number(e.target.value))} title="Overtime hours">
                  {[0, 1, 2, 3, 4, 5, 6].map((h) => <option key={h} value={h}>{h ? `+${h}h OT` : 'No OT'}</option>)}
                </select>
              )}
              <div className="att-segment">
                {STATUSES.map((s) => (
                  <button key={s.value} title={s.full}
                    className={'att-seg-btn ' + s.cls + (m?.status === s.value ? ' on' : '')}
                    onClick={() => setStatus(r.id, s.value)}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {rows.length === 0 && <div className="empty-state"><p>No active employees found.</p></div>}
      </div>
    </>
  );
}
